import { ExcelFileViewer } from "@/components/common/ExcelViewer/ExcelFileViewer"
import { Button } from "@/components/ui/button"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AlertCircle, Download, FileText, RotateCw } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"
import {
  useMatrix,
  useProcessingStatus,
  useReopen,
  useResponseFile,
  useStartRender,
} from "../../../api/questionnaireProcessingApi"
import type { AnswerStatus } from "../../../api/types/questionnaireProcessing"
import { FooterActionBar } from "../../shared/FooterActionBar"
import { McPill } from "../../shared/McPill"
import { ModuleHeader } from "../../shared/ModuleHeader"
import { customerRequestProcessPath } from "../routes"
import { FrameworkChips } from "./FrameworkChips"
import { FrameworkGroupedList } from "./FrameworkGroupedList"
import { ProcessingItemRow } from "./ProcessingItemRow"
import { useProcessingOutletContext } from "./QuestionnaireProcessingLayout"
import { useFrameworkGroups } from "./useFrameworkGroups"

// Backend enum → label key. Falls back to the raw value for any future
// citation type the backend adds before this map is updated.
const CITATION_TYPE_LABEL_KEYS: Record<string, TranslationKeys> = {
  evidence: "MC_QP_REVIEW_CITATION_EVIDENCE",
  declaration: "MC_QP_REVIEW_CITATION_DECLARATION",
}

function citationTypeLabel(
  type: string,
  translate: (key: TranslationKeys) => string,
): string {
  const key = CITATION_TYPE_LABEL_KEYS[type]
  return key ? translate(key) : type
}

// Shown only when a cell has no display value of its own (cell.value is
// null) — the pill then falls back to the answer status itself.
const ANSWER_STATUS_LABEL_KEYS: Record<AnswerStatus, TranslationKeys> = {
  answered: "MC_QP_REVIEW_STATUS_ANSWERED",
  overridden: "MC_QP_REVIEW_STATUS_OVERRIDDEN",
  stale: "MC_QP_REVIEW_STATUS_STALE",
  not_applicable: "MC_QP_REVIEW_STATUS_NOT_APPLICABLE",
  gap_no_data: "MC_QP_REVIEW_STATUS_GAP_NO_DATA",
  gap_pending_review: "MC_QP_REVIEW_STATUS_GAP_PENDING_REVIEW",
}

// Falls back to the raw value for any future status the backend adds before
// this map is updated (same defensive shape as citationTypeLabel above).
function answerStatusLabel(
  status: AnswerStatus,
  translate: (key: TranslationKeys) => string,
): string {
  const key = ANSWER_STATUS_LABEL_KEYS[status]
  return key ? translate(key) : status
}

export function ReviewStep() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const { questionnaireId } = useProcessingOutletContext()
  const { data, isLoading } = useMatrix(questionnaireId)
  const { data: statusData } = useProcessingStatus(questionnaireId)
  const {
    mutate: startRender,
    isPending: renderStarting,
    error: renderError,
  } = useStartRender()
  const { mutate: reopen } = useReopen()
  const status = statusData?.processingStatus ?? null
  const { data: file, error: fileError } = useResponseFile(questionnaireId, {
    enabled: status === "answered" || status === "sent",
  })

  // The Review preview should show the actual file that gets sent, not a
  // reconstruction of the customer's original sheet — so render as soon as
  // this step is reached instead of waiting on a manual "Download" click.
  const renderTriggeredRef = useRef(false)
  useEffect(() => {
    if (status === "review_ready" && !renderTriggeredRef.current) {
      renderTriggeredRef.current = true
      startRender(questionnaireId)
    }
  }, [status, questionnaireId, startRender])

  const cells = useMemo(() => data?.cells ?? [], [data])
  const coverage = data?.coverage
  const isAnswered = status === "answered" || status === "sent"
  const isRendering = status === "rendering" || renderStarting
  const isRenderFailed = status === "failed_render"

  const { groups, registerSection, jumpTo } = useFrameworkGroups(
    cells,
    (cell) => cell.framework,
    translate("MC_QP_FRAMEWORK_OTHER"),
  )

  // Purely cosmetic row selection now — the right panel shows the actual
  // rendered file (no per-cell highlighting to drive anymore).
  const [activeCellId, setActiveCellId] = useState<string | null>(null)

  if (isLoading || !data) {
    return <div className="flex h-full items-center justify-center" />
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="px-6 pb-2 pt-1">
        <ModuleHeader
          title={translate("MC_QP_REVIEW_TITLE")}
          helpArticle={{
            categoryId: "customer-studio",
            articleId: "customer.requests",
          }}
          subtitle={translate("MC_QP_REVIEW_SUBTITLE")}
          right={
            coverage && (
              <McPill tone="success">
                {translate("MC_QP_REVIEW_COVERAGE", {
                  answered: coverage.answered,
                  total: coverage.total,
                  pct: coverage.pctAnswered,
                })}
              </McPill>
            )
          }
        />
      </div>

      <FrameworkChips groups={groups} onJump={jumpTo} />

      <div className="min-h-0 flex-1 px-6 pb-0 pt-2">
        <div className="h-full overflow-hidden rounded-lg border">
          <ResizablePanelGroup orientation="horizontal">
            <ResizablePanel defaultSize={42} minSize={28}>
              <div className="flex h-full min-h-0 flex-col overflow-y-auto">
                {/* Grouped by framework — same shell GapsStep uses, so the
                    two screens read as one consistent list. */}
                <FrameworkGroupedList
                  groups={groups}
                  registerSection={registerSection}
                  keyOf={(cell) => cell.answerId}
                  renderItem={(cell) => (
                    <ProcessingItemRow
                      articleNumber={cell.customerArticleNumber}
                      isActive={activeCellId === cell.answerId}
                      onSelect={() => setActiveCellId(cell.answerId)}
                      topRight={
                        <McPill
                          tone={
                            cell.answerStatus === "answered" ||
                            cell.answerStatus === "overridden"
                              ? "success"
                              : cell.answerStatus === "stale"
                                ? "doubt"
                                : cell.answerStatus === "not_applicable"
                                  ? "muted"
                                  : "danger"
                          }
                        >
                          {cell.value ??
                            answerStatusLabel(cell.answerStatus, translate)}
                        </McPill>
                      }
                    >
                      {cell.citationType && (
                        <p className="text-[11px] text-muted-foreground">
                          {translate("MC_QP_REVIEW_CITATION_SOURCE", {
                            source: citationTypeLabel(
                              cell.citationType,
                              translate,
                            ),
                          })}{" "}
                          · {cell.citationRef}
                        </p>
                      )}
                    </ProcessingItemRow>
                  )}
                />
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={58} minSize={30}>
              <div className="flex h-full min-h-0 flex-col bg-background">
                <div className="flex h-[50px] shrink-0 items-center gap-2.5 border-b bg-muted/40 px-4">
                  <span className="inline-flex items-center gap-2 whitespace-nowrap text-[12px] text-muted-foreground">
                    <FileText size={15} />
                    {translate("MC_QP_REVIEW_ANSWERED_FILE")}
                  </span>
                  {file?.fileName && (
                    <span className="font-mono max-w-[240px] truncate text-[12px] text-muted-foreground">
                      {file.fileName}
                    </span>
                  )}
                </div>
                <div className="min-h-0 flex-1">
                  {file?.url ? (
                    <ExcelFileViewer file={file.url} />
                  ) : (isRenderFailed || fileError || renderError) &&
                    !isRendering ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-[13px] text-muted-foreground">
                      <AlertCircle size={20} className="text-red1" />
                      {translate("MC_QP_REVIEW_RENDER_FAILED")}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startRender(questionnaireId)}
                      >
                        <RotateCw size={13} className="mr-1.5" />
                        {translate("MC_QP_REVIEW_RETRY_RENDER")}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-primary" />
                    </div>
                  )}
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </div>

      <FooterActionBar left={<p>{translate("MC_QP_REVIEW_FOOTER_NOTE")}</p>}>
        {status === "sent" && (
          <McPill tone="success">
            {translate("MC_QP_REVIEW_SENT_LABEL", {
              date: statusData?.sentAt ?? "",
            })}
          </McPill>
        )}
        {isAnswered && status !== "sent" && (
          <Button
            data-agent-target="request.reopen"
            variant="outline"
            onClick={() =>
              reopen(questionnaireId, {
                onSuccess: () =>
                  navigate(customerRequestProcessPath(questionnaireId, "gaps")),
              })
            }
          >
            {translate("MC_QP_REVIEW_REOPEN_BUTTON")}
          </Button>
        )}
        <Button
          data-agent-target="request.download"
          className="bg-yellow1 text-black hover:bg-yellow2"
          disabled={isRendering && !file}
          onClick={() => {
            if (file) window.open(file.url, "_blank", "noopener")
            else startRender(questionnaireId)
          }}
        >
          <Download className="mr-2 h-4 w-4" />
          {translate("MC_QP_REVIEW_DOWNLOAD_BUTTON")}
        </Button>
      </FooterActionBar>
    </div>
  )
}
